import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Phone, 
  PhoneCall, 
  PhoneOff, 
  Mic, 
  MicOff, 
  X, 
  CheckCircle2, 
  AlertCircle, 
  Headphones, 
  Radio, 
  Users, 
  Search, 
  Zap,
  Sparkles,
  Bot,
  Volume2,
  VolumeX,
  Send,
  MessageSquare,
  Loader2,
  Clock
} from 'lucide-react';
import { io } from 'socket.io-client';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../utils/api';
import { useWebRTCCall } from '../../hooks/useWebRTCCall';
import { ringtone, waitingTonePlayer, getSavedWaitingTone, saveWaitingToneSetting, WAITING_TONE_PRESETS } from '../../utils/callAudio';
import { 
  AI_VOICE_PERSONAS, 
  getSavedVoicePersona, 
  saveVoiceSettings,
  playNeuralAudio, 
  stopAiVoice 
} from '../../utils/aiVoiceConfig';

const FloatingCallWidget = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const socketRef = useRef(null);

  // Widget panel states
  const [isOpen, setIsOpen] = useState(false);
  const [callerPhone, setCallerPhone] = useState(user?.phone || '');
  const [callReason, setCallReason] = useState('');
  const [callbackPhone, setCallbackPhone] = useState(user?.phone || '');
  const [callbackNotes, setCallbackNotes] = useState('');
  const [isSubmittingCallback, setIsSubmittingCallback] = useState(false);
  const [callbackSuccess, setCallbackSuccess] = useState(false);
  const [offlineNotice, setOfflineNotice] = useState(false);
  const [adminCallbacks, setAdminCallbacks] = useState([]);

  // Intercom & Team States
  const [activeTab, setActiveTab] = useState('console'); // 'console' | 'intercom'
  const [onlineTeam, setOnlineTeam] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [intercomSearch, setIntercomSearch] = useState('');
  const [activeCallInfo, setActiveCallInfo] = useState(null);

  const isTeam = ['admin', 'manager', 'developer', 'marketing', 'design', 'support'].includes(user?.role?.toLowerCase());
  const isAdminOrManager = ['admin', 'manager'].includes(user?.role?.toLowerCase());

  // Fetch pending callbacks for admins
  useEffect(() => {
    if (!isAdminOrManager) return;
    const fetchCallbacks = async () => {
      try {
        const res = await axios.get(getApiUrl('/api/calls/callbacks?status=pending'));
        setAdminCallbacks(res.data.data || []);
      } catch (e) {}
    };
    fetchCallbacks();
    const interval = setInterval(fetchCallbacks, 15000);
    return () => clearInterval(interval);
  }, [isAdminOrManager]);

  // Fetch team directory for intercom calling
  useEffect(() => {
    if (!isTeam) return;
    const fetchTeam = async () => {
      try {
        const res = await axios.get(getApiUrl('/api/users/team-staff'));
        setTeamMembers(res.data.data || []);
      } catch (e) {}
    };
    fetchTeam();
  }, [isTeam]);

  // Incoming call state
  const [incomingCall, setIncomingCall] = useState(null);

  // Active call duration and state
  const {
    callState,
    setCallState,
    callDuration,
    isMuted,
    toggleMute,
    startCall,
    answerCall,
    transferToPeer,
    rejectCall,
    endCall,
    cleanup
  } = useWebRTCCall({
    socketRef,
    onCallConnected: () => {
      if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
      ringtone.stop();
      waitingTonePlayer.stop();
      stopAiVoice();
      setIncomingCall(null);
      setIsOpen(false);
    },
    onCallEnded: () => {
      if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
      ringtone.stop();
      waitingTonePlayer.stop();
      stopAiVoice();
      setIncomingCall(null);
      setActiveCallInfo(null);
      resetCallForm();
    },
    onCallDeclined: () => {
      ringtone.stop();
      setIncomingCall(null);
      const wasIntercom = activeCallInfo?.isIntercom;
      setActiveCallInfo(null);
      if (!wasIntercom) {
        if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
        holdTimeoutRef.current = setTimeout(() => {
          waitingTonePlayer.stop();
          stopAiVoice();
          startAiVoiceCallRef.current?.();
        }, 8500);
      } else {
        waitingTonePlayer.stop();
        stopAiVoice();
        toast('Call was declined.', { icon: '📴' });
      }
    },
    onCallTimeout: () => {
      if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
      ringtone.stop();
      waitingTonePlayer.stop();
      stopAiVoice();
      setIncomingCall(null);
      const wasIntercom = activeCallInfo?.isIntercom;
      setActiveCallInfo(null);
      if (!wasIntercom) {
        toast('Connecting you to Epic AI...', { icon: '🤖', duration: 4000 });
        startAiVoiceCallRef.current?.();
      } else {
        toast('No answer from team member.', { icon: '📴' });
      }
    }
  });

  const [activeAiCalls, setActiveAiCalls] = useState([]);
  const transferToPeerRef = useRef(transferToPeer);
  useEffect(() => {
    transferToPeerRef.current = transferToPeer;
  }, [transferToPeer]);

  const [guestName, setGuestName] = useState('');
  const userRef = useRef(user);
  const isCallActiveRef = useRef(false);

  // Form value refs so AI voice receptionist always reads latest user input without closure staleness
  const guestNameRef = useRef('');
  const callerPhoneRef = useRef('');
  const callReasonRef = useRef('');

  useEffect(() => { guestNameRef.current = guestName; }, [guestName]);
  useEffect(() => { callerPhoneRef.current = callerPhone; }, [callerPhone]);
  useEffect(() => { callReasonRef.current = callReason; }, [callReason]);

  // Helper to clear the call form completely after any call ends
  const resetCallForm = useCallback(() => {
    setGuestName('');
    setCallerPhone('');
    setCallReason('');
    setCallbackPhone('');
    setCallbackNotes('');
  }, []);

  useEffect(() => {
    userRef.current = user;
    isCallActiveRef.current = (callState === 'calling' || callState === 'connected');
  }, [user, callState]);

  // --- EPIC AI VOICE RECEPTIONIST STATES ---
  const [isAiCallActive, setIsAiCallActive] = useState(false);
  const [aiCallStatus, setAiCallStatus] = useState('idle'); // 'connecting' | 'listening' | 'thinking' | 'speaking' | 'ended'
  const [aiTranscript, setAiTranscript] = useState([]);
  const [liveUserSpeech, setLiveUserSpeech] = useState('');
  const [aiDuration, setAiDuration] = useState(0);
  const [isAiMuted, setIsAiMuted] = useState(false);
  const [showAiTextInput, setShowAiTextInput] = useState(false);
  const [aiTextFallback, setAiTextFallback] = useState('');
  const [isAiSaving, setIsAiSaving] = useState(false);
  const [aiSaveSuccess, setAiSaveSuccess] = useState(false);

  // Auto-redirect timer state when call ends
  const [redirectCountdown, setRedirectCountdown] = useState(null);
  const redirectTimerRef = useRef(null);

  // Dynamic AI Voice & Orchestral Waiting Tone settings — loaded from DB (system-wide)
  const [voicePersona, setVoicePersona] = useState(getSavedVoicePersona());
  const [waitingTone, setWaitingTone] = useState(getSavedWaitingTone());

  // Fetch voice persona & waiting tone from DB on mount so every caller uses the admin-set audio
  useEffect(() => {
    axios.get(getApiUrl('/api/settings'))
      .then(res => {
        const persona = res.data?.data?.aiVoicePersona;
        if (persona && AI_VOICE_PERSONAS[persona]) {
          setVoicePersona(persona);
          localStorage.setItem('epicedge_ai_voice_persona', persona);
        }
        const tone = res.data?.data?.callWaitingTone;
        if (tone && WAITING_TONE_PRESETS[tone]) {
          setWaitingTone(tone);
          localStorage.setItem('epicedge_waiting_tone', tone);
        }
      })
      .catch(() => {}); // silent fail — use localStorage default
  }, []);

  useEffect(() => {
    const handleVoiceChange = (e) => {
      const { personaKey } = e.detail || {};
      if (personaKey) setVoicePersona(personaKey);
    };
    const handleToneChange = (e) => {
      const { toneKey } = e.detail || {};
      if (toneKey) setWaitingTone(toneKey);
    };
    window.addEventListener('epic_voice_settings_changed', handleVoiceChange);
    window.addEventListener('epic_waiting_tone_changed', handleToneChange);
    return () => {
      window.removeEventListener('epic_voice_settings_changed', handleVoiceChange);
      window.removeEventListener('epic_waiting_tone_changed', handleToneChange);
    };
  }, []);

  const aiTranscriptRef = useRef([]);
  const holdTimeoutRef = useRef(null);
  const isAiCallActiveRef = useRef(false);
  const isAiMutedRef = useRef(false);
  const aiDurationRef = useRef(0);
  const isAiSpeakingRef = useRef(false);
  const isAiThinkingRef = useRef(false);
  const isRecognitionRunningRef = useRef(false);
  const recognitionInstanceRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const aiTimerRef = useRef(null);
  const aiStreamRef = useRef(null);
  const speechTimeoutRef = useRef(null);

  const turnRecorderRef = useRef(null);
  const turnChunksRef = useRef([]);
  const vadIntervalRef = useRef(null);
  const audioContextRef = useRef(null);
  const callAudioContextRef = useRef(null);
  const mixedStreamDestinationRef = useRef(null);
  const latestSpokenTextRef = useRef('');

  const speakAiReplyRef = useRef(null);
  const startListeningRef = useRef(null);
  const handleUserVoiceTurnRef = useRef(null);
  const startAiVoiceCallRef = useRef(null);
  const endAiVoiceCallRef = useRef(null);

  useEffect(() => {
    aiTranscriptRef.current = aiTranscript;
  }, [aiTranscript]);

  useEffect(() => {
    isAiCallActiveRef.current = isAiCallActive;
  }, [isAiCallActive]);

  useEffect(() => {
    isAiMutedRef.current = isAiMuted;
  }, [isAiMuted]);

  useEffect(() => {
    aiDurationRef.current = aiDuration;
  }, [aiDuration]);

  useEffect(() => {
    isAiSpeakingRef.current = (aiCallStatus === 'speaking');
  }, [aiCallStatus]);

  // Preload speech synthesis voices
  useEffect(() => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
  }, []);

  const getFemaleVoice = useCallback(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return null;
    const voices = window.speechSynthesis.getVoices() || [];
    const preferred = voices.find(v => 
      /Google US English|Samantha|Microsoft Zira|Victoria|Karen|Fiona|Jenny|Natural/i.test(v.name)
    );
    if (preferred) return preferred;

    const anyFemale = voices.find(v => 
      (/female/i.test(v.name) || /woman/i.test(v.name)) && /^en/i.test(v.lang)
    );
    if (anyFemale) return anyFemale;

    const anyEnglish = voices.find(v => /^en/i.test(v.lang));
    return anyEnglish || voices[0] || null;
  }, []);

  const startListening = useCallback(() => {
    if (
      !isAiCallActiveRef.current || 
      isAiMutedRef.current || 
      isAiSpeakingRef.current || 
      isAiThinkingRef.current
    ) {
      return;
    }

    setAiCallStatus('listening');

    // 1. Clean up any existing turn recorder & VAD interval
    if (vadIntervalRef.current) {
      clearInterval(vadIntervalRef.current);
      vadIntervalRef.current = null;
    }
    if (turnRecorderRef.current && turnRecorderRef.current.state !== 'inactive') {
      try {
        turnRecorderRef.current.onstop = null;
        turnRecorderRef.current.stop(); 
      } catch (e) {}
    }

    // 2. Start Turn MediaRecorder on the live microphone stream!
    if (aiStreamRef.current && window.MediaRecorder) {
      try {
        turnChunksRef.current = [];
        // Pick the best codec this device supports (Whisper accepts webm, mp4, ogg)
        const turnMimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : '';
        const turnRec = new MediaRecorder(
          aiStreamRef.current,
          turnMimeType ? { mimeType: turnMimeType } : undefined
        );
        const turnBlobType = turnMimeType || 'audio/webm';
        turnRec.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) {
            turnChunksRef.current.push(e.data);
          }
        };
        turnRec.start(200);
        turnRecorderRef.current = turnRec;

        // 3. AudioContext volume detector for Voice Activity Detection
        try {
          if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
          }
          const ctx = audioContextRef.current;
          if (ctx.state === 'suspended') {
            ctx.resume();
          }

          const source = ctx.createMediaStreamSource(aiStreamRef.current);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 256;
          source.connect(analyser);

          const dataArray = new Uint8Array(analyser.frequencyBinCount);
          let speechCount = 0;
          let silenceCount = 0;

          vadIntervalRef.current = setInterval(() => {
            if (isAiSpeakingRef.current || isAiThinkingRef.current || !isAiCallActiveRef.current) {
              return;
            }

            analyser.getByteFrequencyData(dataArray);
            let sum = 0;
            for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
            const avg = sum / dataArray.length;

            if (avg > 15) { // Voice detected! (Threshold tuned to 15 to reject ambient room hiss)
              speechCount++;
              silenceCount = 0;
              setLiveUserSpeech('Hearing you... speak freely');
            } else if (speechCount > 3) {
              silenceCount++;
              // Natural conversational pause detection of 1.5s so callers can speak their email/thoughts without interruption
              if (silenceCount >= 15) {
                clearInterval(vadIntervalRef.current);
                vadIntervalRef.current = null;
                setLiveUserSpeech('');

                // If Web Speech already captured user text, prioritize it immediately without heavy audio upload!
                if (latestSpokenTextRef.current && latestSpokenTextRef.current.trim().length > 2) {
                  const captured = latestSpokenTextRef.current.trim();
                  latestSpokenTextRef.current = '';
                  try { recognitionInstanceRef.current?.abort(); } catch (e) {}
                  isRecognitionRunningRef.current = false;
                  handleUserVoiceTurnRef.current?.({ userText: captured });
                  return;
                }

                if (turnRec.state !== 'inactive') {
                  turnRec.onstop = () => {
                    const audioBlob = new Blob(turnChunksRef.current, { type: turnBlobType });
                    if (audioBlob.size > 3000) {
                      handleUserVoiceTurnRef.current?.({ audioBlob });
                    } else {
                      startListeningRef.current?.();
                    }
                  };
                  try { turnRec.stop(); } catch (e) {}
                }
              }
            }
          }, 100);
        } catch (audioErr) {
          console.warn('AudioContext volume detector notice:', audioErr);
        }
      } catch (err) {
        console.warn('Turn recorder setup notice:', err);
      }
    }

    // 4. Parallel Web Speech Recognition (fast-path if supported)
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition && !isRecognitionRunningRef.current) {
      try {
        if (recognitionInstanceRef.current) {
          try { recognitionInstanceRef.current.abort(); } catch (e) {}
          recognitionInstanceRef.current = null;
        }

        const rec = new SpeechRecognition();
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang = 'en-US';

        let speechTimer = null;

        rec.onstart = () => {
          isRecognitionRunningRef.current = true;
        };

        rec.onresult = (event) => {
          let text = '';
          for (let i = 0; i < event.results.length; i++) {
            text += event.results[i][0].transcript + ' ';
          }
          const spoken = text.trim();
          if (spoken) {
            latestSpokenTextRef.current = spoken;
            setLiveUserSpeech(spoken);
            if (speechTimer) clearTimeout(speechTimer);
            // Natural 1.5s pause allows callers to breathe, formulate thoughts, or speak emails without being cut off
            speechTimer = setTimeout(() => {
              if (spoken.length > 2) {
                if (vadIntervalRef.current) {
                  clearInterval(vadIntervalRef.current);
                  vadIntervalRef.current = null;
                }
                setLiveUserSpeech('');
                try { rec.abort(); } catch (e) {}
                isRecognitionRunningRef.current = false;
                latestSpokenTextRef.current = '';
                handleUserVoiceTurnRef.current?.({ userText: spoken });
              }
            }, 1500);
          }
        };

        rec.onerror = () => {
          isRecognitionRunningRef.current = false;
          // Restart listening so the user isn't left in silence on unsupported browsers
          if (isAiCallActiveRef.current && !isAiSpeakingRef.current && !isAiThinkingRef.current) {
            setTimeout(() => {
              if (isAiCallActiveRef.current && !isAiSpeakingRef.current && !isAiThinkingRef.current) {
                startListeningRef.current?.();
              }
            }, 400);
          }
        };

        rec.onend = () => {
          isRecognitionRunningRef.current = false;
          // Restart listening if still in an active AI call (handles auto-stop on some browsers)
          if (isAiCallActiveRef.current && !isAiSpeakingRef.current && !isAiThinkingRef.current) {
            setTimeout(() => {
              if (isAiCallActiveRef.current && !isAiSpeakingRef.current && !isAiThinkingRef.current) {
                startListeningRef.current?.();
              }
            }, 400);
          }
        };

        rec.start();
        recognitionInstanceRef.current = rec;
      } catch (e) {
        isRecognitionRunningRef.current = false;
      }
    }
  }, []);

  const speakAiReply = useCallback((text, onEnd) => {
    // 1. Stop any turn recording while AI is speaking so it doesn't hear itself
    isAiSpeakingRef.current = true;
    if (vadIntervalRef.current) {
      clearInterval(vadIntervalRef.current);
      vadIntervalRef.current = null;
    }
    if (turnRecorderRef.current && turnRecorderRef.current.state !== 'inactive') {
      try { 
        turnRecorderRef.current.onstop = null;
        turnRecorderRef.current.stop(); 
      } catch (e) {}
    }
    if (recognitionInstanceRef.current) {
      try {
        recognitionInstanceRef.current.onresult = null;
        recognitionInstanceRef.current.onerror = null;
        recognitionInstanceRef.current.onend = null;
        recognitionInstanceRef.current.abort();
      } catch (e) {}
      recognitionInstanceRef.current = null;
    }
    isRecognitionRunningRef.current = false;

    setAiCallStatus('speaking');
    setLiveUserSpeech('');
    isAiSpeakingRef.current = true;

    // Play real high-fidelity Neural AI Voice (Siri, Alexa, Jarvis, Cortana)
    // Routes to caller's speakers AND feeds directly into call recording stream!
    playNeuralAudio(
      text, 
      voicePersona, 
      () => {
        isAiSpeakingRef.current = false;
        if (onEnd) {
          // If onEnd is passed (call is concluding), execute onEnd and DO NOT restart listening!
          onEnd();
        } else {
          setAiCallStatus('listening');
          setTimeout(() => {
            if (isAiCallActiveRef.current && !isAiMutedRef.current && !isAiSpeakingRef.current && !isAiThinkingRef.current) {
              startListeningRef.current?.();
            }
          }, 300);
        }
      },
      callAudioContextRef.current,
      mixedStreamDestinationRef.current
    );
  }, [voicePersona]);

  const handleUserVoiceTurn = useCallback(async ({ userText, audioBlob }) => {
    if (!userText && !audioBlob) return;
    if (isAiThinkingRef.current || isAiSpeakingRef.current) return;

    isAiThinkingRef.current = true;
    setAiCallStatus('thinking');

    try {
      const history = aiTranscriptRef.current.map(item => ({
        role: item.role === 'user' ? 'user' : 'model',
        text: item.text
      }));

      const activePhone = callerPhone?.trim() || callbackPhone?.trim() || user?.phone || '';
      const activeName = user?.name || guestName?.trim() || 'Website Visitor';
      const activeReason = (callReason || '').trim();

      let res;
      if (audioBlob) {
        const formData = new FormData();
        formData.append('audio', audioBlob, 'turn.webm');
        formData.append('history', JSON.stringify(history));
        formData.append('callerName', activeName);
        formData.append('callerPhone', activePhone);
        formData.append('callReason', activeReason);

        res = await axios.post(getApiUrl('/api/calls/ai-voice-turn'), formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        res = await axios.post(getApiUrl('/api/calls/ai-voice-turn'), {
          message: userText,
          history,
          callerName: activeName,
          callerPhone: activePhone,
          callReason: activeReason
        });
      }

      if (res.data?.noSpeech) {
        // Silence or background click, seamlessly resume listening
        isAiThinkingRef.current = false;
        setAiCallStatus('listening');
        setTimeout(() => {
          if (isAiCallActiveRef.current && !isAiSpeakingRef.current && !isAiThinkingRef.current) {
            startListeningRef.current?.();
          }
        }, 300);
        return;
      }

      const recognizedSpeech = res.data?.userSpeech || userText || '';
      if (recognizedSpeech) {
        setAiTranscript(prev => [...prev, { role: 'user', text: recognizedSpeech, time: new Date() }]);
      }

      const reply = res.data?.reply || "Thank you! I have noted that and our team will get back to you.";
      setAiTranscript(prev => [...prev, { role: 'bot', text: reply, time: new Date() }]);

      isAiThinkingRef.current = false;

      // Detect if the caller is concluding the call or saying goodbye
      // ONLY end if the caller explicitly wraps up with a short closing phrase
      // (NEVER when "that's all" is part of a longer sentence explaining requirements/features!)
      const trimmedSpeech = (recognizedSpeech || '').trim().toLowerCase();
      const isShortSpeech = trimmedSpeech.length <= 45;

      const isExplicitCallerGoodbye = isShortSpeech && (
        /^(no[,.\s]+)?(goodbye|bye|bye\s*bye|thanks\s*bye|thank\s*you\s*bye|have\s*a\s*(good|great|nice)\s*day)[.!]?$/i.test(trimmedSpeech) ||
        /^(no[,.\s]+)?(that'?s\s*all|nothing\s*else|all\s*for\s*now|no\s*thanks|no\s*thank\s*you)[,.\s]*(thank\s*you|thanks)?[.!]?$/i.test(trimmedSpeech)
      );

      const isAiFarewell = /\b(goodbye|have a (great|good|wonderful) day|take care|have a good one)\b/i.test(reply) && (
        isShortSpeech && /\b(no|nope|nah|that'?s all|nothing|bye|thank you|thanks)\b/i.test(trimmedSpeech)
      );

      const isCallEnding = isExplicitCallerGoodbye || isAiFarewell;

      if (isCallEnding) {
        // AI speaks farewell and automatically hangs up when done speaking!
        speakAiReplyRef.current?.(reply, () => {
          setTimeout(() => {
            if (isAiCallActiveRef.current) {
              if (endAiVoiceCallRef.current) {
                endAiVoiceCallRef.current();
              } else {
                endAiVoiceCall();
              }
            }
          }, 1200);
        });
      } else {
        speakAiReplyRef.current?.(reply);
      }
    } catch (err) {
      console.error('Error during AI voice turn:', err);
      isAiThinkingRef.current = false;
      setAiCallStatus('listening');
      setTimeout(() => {
        if (isAiCallActiveRef.current && !isAiSpeakingRef.current && !isAiThinkingRef.current) {
          startListeningRef.current?.();
        }
      }, 300);
    }
  }, [callerPhone, callbackPhone, user?.phone, user?.name, guestName, callReason]);

  const startAiVoiceCall = useCallback(async () => {
    if (isAiCallActiveRef.current) return; // Prevent duplicate trigger

    ringtone.stop();
    waitingTonePlayer.stop();
    stopAiVoice();
    cleanup();
    setOfflineNotice(false);
    setIsOpen(false);
    setIsAiCallActive(true);
    isAiCallActiveRef.current = true;
    setAiCallStatus('connecting');
    setAiDuration(0);
    setLiveUserSpeech('');
    setShowAiTextInput(false);
    setAiSaveSuccess(false);

    const activeName = (guestNameRef.current || guestName || user?.name || '').trim();
    const activeReason = (callReasonRef.current || callReason || '').trim();

    let greeting;
    if (activeName && activeReason) {
      greeting = `Hello ${activeName}! Thank you so much for calling EpicEdge Creative — we're delighted to connect with you. I see you're inquiring about ${activeReason}. I'm Epic AI, and I'm right here to take great care of you today. How can I help bring your vision to life?`;
    } else if (activeName) {
      greeting = `Hello ${activeName}! Thank you so much for calling EpicEdge Creative — we truly appreciate you reaching out. I'm Epic AI, your dedicated assistant, and I'm ready to help you with whatever you need. How can I assist you today?`;
    } else {
      greeting = "Hello and welcome to EpicEdge Creative! Thank you so much for calling us today — we really appreciate you reaching out. I'm Epic AI, your dedicated assistant, and I'm right here to help you. How can I assist you today?";
    }

    setAiTranscript([{ role: 'bot', text: greeting, time: new Date() }]);

    // Notify connected admins that an AI call is active (for live takeover)
    socketRef.current?.emit('ai_call_started', {
      callerName: activeName,
      callerPhone: callerPhoneRef.current || callerPhone || user?.phone,
      reason: activeReason
    });

    if (aiTimerRef.current) clearInterval(aiTimerRef.current);
    aiTimerRef.current = setInterval(() => {
      setAiDuration(d => d + 1);
    }, 1000);

    // Two-Way Audio Stream & Mixed Call Recorder (Caller Mic + Epic AI Voice)
    try {
      audioChunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { 
          echoCancellation: true, 
          noiseSuppression: true,
          autoGainControl: true 
        } 
      });
      aiStreamRef.current = stream;

      // 1. AudioContext for Web Audio mixing
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      callAudioContextRef.current = ctx;
      if (ctx.state === 'suspended') {
        await ctx.resume().catch(() => {});
      }

      // 2. Create the mixed MediaStreamDestination (captures caller mic + AI voice)
      const mixedDest = ctx.createMediaStreamDestination();
      mixedStreamDestinationRef.current = mixedDest;

      // 3. Connect caller microphone stream into mixed destination
      const micSource = ctx.createMediaStreamSource(stream);
      micSource.connect(mixedDest);

      // 4. Start MediaRecorder on the MIXED stream
      if (window.MediaRecorder) {
        let options = undefined;
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          options = { mimeType: 'audio/webm;codecs=opus' };
        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
          options = { mimeType: 'audio/webm' };
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          options = { mimeType: 'audio/mp4' };
        }

        const recorder = new MediaRecorder(mixedDest.stream, options);
        recorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) {
            audioChunksRef.current.push(e.data);
          }
        };
        recorder.start(250);
        mediaRecorderRef.current = recorder;
      }
    } catch (micErr) {
      console.warn('Microphone / Web Audio stream notice:', micErr);
    }

    // Speak initial greeting then transition to listening
    speakAiReply(greeting);
  }, [cleanup, speakAiReply, user?.name, guestName, callReason]);

  // Keep refs current to prevent temporal dead zone / circular dependency closures
  useEffect(() => {
    speakAiReplyRef.current = speakAiReply;
    startListeningRef.current = startListening;
    handleUserVoiceTurnRef.current = handleUserVoiceTurn;
    startAiVoiceCallRef.current = startAiVoiceCall;
  }, [speakAiReply, startListening, handleUserVoiceTurn, startAiVoiceCall]);

  const endAiVoiceCall = useCallback(async () => {
    socketRef.current?.emit('ai_call_ended');
    isAiCallActiveRef.current = false;
    isRecognitionRunningRef.current = false;
    stopAiVoice();
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    if (vadIntervalRef.current) {
      clearInterval(vadIntervalRef.current);
      vadIntervalRef.current = null;
    }
    if (turnRecorderRef.current && turnRecorderRef.current.state !== 'inactive') {
      try { turnRecorderRef.current.stop(); } catch (e) {}
      turnRecorderRef.current = null;
    }
    if (recognitionInstanceRef.current) {
      try {
        recognitionInstanceRef.current.onresult = null;
        recognitionInstanceRef.current.onerror = null;
        recognitionInstanceRef.current.onend = null;
        recognitionInstanceRef.current.abort();
      } catch (e) {}
      recognitionInstanceRef.current = null;
    }
    if (aiTimerRef.current) {
      clearInterval(aiTimerRef.current);
      aiTimerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        if (typeof mediaRecorderRef.current.requestData === 'function') {
          mediaRecorderRef.current.requestData();
        }
        mediaRecorderRef.current.stop(); 
      } catch (e) {}
    }
    if (aiStreamRef.current) {
      aiStreamRef.current.getTracks().forEach(t => t.stop());
      aiStreamRef.current = null;
    }
    if (callAudioContextRef.current && callAudioContextRef.current.state !== 'closed') {
      try {
        callAudioContextRef.current.close().catch(() => {});
      } catch (e) {}
    }

    // Capture final details before resetting form
    const finalPhone = callerPhoneRef.current?.trim() || callerPhone?.trim() || callbackPhone?.trim() || user?.phone || '';
    const finalName = guestNameRef.current?.trim() || guestName?.trim() || user?.name || 'Website Visitor';
    const finalReason = callReasonRef.current?.trim() || callReason?.trim() || '';

    setAiCallStatus('ended');
    setIsAiSaving(true);

    // Reset caller input fields so the form clears itself cleanly
    resetCallForm();

    // Auto-redirect to homepage after 3 seconds
    setRedirectCountdown(3);
    if (redirectTimerRef.current) clearInterval(redirectTimerRef.current);
    redirectTimerRef.current = setInterval(() => {
      setRedirectCountdown(prev => {
        if (prev === null || prev <= 1) {
          clearInterval(redirectTimerRef.current);
          setIsAiCallActive(false);
          setAiCallStatus('idle');
          resetCallForm();
          navigate('/');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    setTimeout(async () => {
      try {
        const formData = new FormData();
        formData.append('callerName', finalName);
        formData.append('callerPhone', finalPhone);
        formData.append('callerEmail', user?.email || '');
        formData.append('callerId', user?._id || '');
        formData.append('callerRole', user?.role || 'client');
        formData.append('duration', aiDurationRef.current || 0);
        formData.append('transcript', JSON.stringify(aiTranscriptRef.current));
        formData.append('reason', finalReason || 'AI Voice Consultation');

        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          formData.append('audio', audioBlob, 'recording.webm');
        }

        await axios.post(getApiUrl('/api/calls/ai-session'), formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        setIsAiSaving(false);
        setAiSaveSuccess(true);
      } catch (err) {
        console.error('Error saving AI voice session:', err);
        setIsAiSaving(false);
        setAiSaveSuccess(true);
      }
    }, 400);
  }, [callerPhone, callbackPhone, user?.phone, user?.name, user?.email, user?._id, user?.role, guestName]);

  useEffect(() => {
    endAiVoiceCallRef.current = endAiVoiceCall;
  }, [endAiVoiceCall]);

  // Update user registration in place without tearing down the socket
  useEffect(() => {
    if (socketRef.current?.connected && isAuthenticated && user) {
      socketRef.current.emit('register_user', {
        userId: user._id,
        name: user.name,
        role: user.role,
        email: user.email,
        phone: user.phone
      });
    }
  }, [user?._id, user?.name, user?.role, isAuthenticated]);

  // 1. Establish Socket connection ONCE and NEVER disconnect during an active call
  useEffect(() => {
    const socketUrl = getApiUrl('');
    const socket = io(socketUrl, {
      path: '/api/socket.io',
      transports: ['polling', 'websocket'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('📡 Connected to WebRTC voice signaling server');
      const currentUser = userRef.current;
      if (isAuthenticated && currentUser) {
        socket.emit('register_user', {
          userId: currentUser._id,
          name: currentUser.name,
          role: currentUser.role,
          email: currentUser.email,
          phone: currentUser.phone
        });
      } else {
        socket.emit('register_user', {
          userId: null,
          name: 'Guest Visitor',
          role: 'guest',
          email: null
        });
      }

      if (isTeam) {
        socket.emit('get_team_presence');
        socket.emit('get_active_ai_calls');
      }
    });

    // Listen for live team presence updates
    socket.on('team_presence_update', (onlineList) => {
      setOnlineTeam(onlineList || []);
    });

    // Listen for active AI calls update (for team/admins)
    socket.on('active_ai_calls_update', (calls) => {
      setActiveAiCalls(calls || []);
    });

    // Caller receives live takeover transfer to admin
    socket.on('ai_call_takeover', async ({ adminSocketId, adminName }) => {
      console.log('⚡ Admin taking over AI call:', adminName);
      toast.success(`Transferring call to ${adminName || 'team lead'}...`, { icon: '🎧', duration: 4000 });

      // 1. Spoken audio notification
      const transferMsg = `One moment please, transferring you to ${adminName || 'our team lead'} right now.`;
      speakAiReplyRef.current?.(transferMsg);

      // 2. Shut down AI voice loop cleanly
      stopAiVoice();
      isAiCallActiveRef.current = false;
      setIsAiCallActive(false);
      setAiCallStatus('idle');
      if (recognitionInstanceRef.current) {
        try { recognitionInstanceRef.current.abort(); } catch (e) {}
        recognitionInstanceRef.current = null;
      }
      if (vadIntervalRef.current) {
        clearInterval(vadIntervalRef.current);
        vadIntervalRef.current = null;
      }
      if (turnRecorderRef.current && turnRecorderRef.current.state !== 'inactive') {
        try { turnRecorderRef.current.stop(); } catch (e) {}
        turnRecorderRef.current = null;
      }
      if (aiTimerRef.current) {
        clearInterval(aiTimerRef.current);
        aiTimerRef.current = null;
      }

      // 3. Set active live call info
      setActiveCallInfo({
        isIntercom: false,
        peerName: adminName || 'Dennis (Support Lead)',
        targetSocketId: adminSocketId
      });

      // 4. Hand off WebRTC stream to admin
      setTimeout(() => {
        transferToPeerRef.current?.(adminSocketId);
      }, 1000);
    });

    socket.on('takeover_failed', ({ message }) => {
      toast.error(message || 'Failed to take over call.', { icon: '❌' });
    });

    // Listen for incoming calls
    socket.on('incoming_call', (callData) => {
      console.log('🔔 Incoming call received:', callData);
      setIncomingCall(callData);
      ringtone.start();
    });

    // Stop incoming call ring if caller cancels or times out
    socket.on('call_cancelled', () => {
      console.log('📴 Caller cancelled or timed out.');
      ringtone.stop();
      setIncomingCall(null);
    });

    // Listen for offline/busy notification
    socket.on('call_failed', ({ reason }) => {
      ringtone.stop();
      cleanup();
      setActiveCallInfo(null);
      if (reason === 'offline' || reason === 'busy') {
        if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
        // Play full 9.5s luxury orchestral waiting tone + brand welcome, then transition smoothly to Epic AI!
        holdTimeoutRef.current = setTimeout(() => {
          waitingTonePlayer.stop();
          stopAiVoice();
          startAiVoiceCallRef.current?.();
        }, 9500);
      }
    });

    // Global event listener for custom "Call Client" / "Intercom" clicks from other pages
    const handleCustomCall = (e) => {
      const { targetUserId, targetName, projectId, reason, isIntercom } = e.detail || {};
      if (targetUserId) {
        handleStartCall(targetUserId, reason || `Call to ${targetName || 'User'}`, targetName, !!isIntercom);
      }
    };
    window.addEventListener('epic_start_call', handleCustomCall);

    return () => {
      window.removeEventListener('epic_start_call', handleCustomCall);
      ringtone.stop();
      // NEVER disconnect if user is on an active call!
      if (!isCallActiveRef.current) {
        socket.disconnect();
        socketRef.current = null;
      }
    };
  }, [isAuthenticated, user?._id, isTeam]);

  // Admin Action: Take Over Active AI Call
  const handleTakeoverCall = useCallback(async (callerSocketId, callerName) => {
    if (!socketRef.current) return;
    toast.success(`Taking over call with ${callerName || 'Client'}...`, { icon: '⚡' });
    setActiveCallInfo({
      isIntercom: false,
      peerName: callerName || 'Client Caller',
      targetSocketId: callerSocketId
    });
    setIsOpen(false);
    await answerCall(callerSocketId, true);
    socketRef.current.emit('takeover_ai_call', { callerSocketId });
  }, [answerCall]);

  // Keep phone in sync with user profile if available
  useEffect(() => {
    if (user?.phone && !callerPhone) {
      setCallerPhone(user.phone);
    }
  }, [user]);

  // Handle outgoing call initiation (Client Call OR Intercom)
  const handleStartCall = useCallback(async (
    targetUserId = null, 
    customReason = null, 
    targetName = null, 
    isIntercom = false
  ) => {
    // Validate name, phone number, and call reason as mandatory for inbound calls
    if (!isIntercom) {
      if (!isAuthenticated && !guestName.trim()) {
        toast.error('Please enter your name to connect the call.', { icon: '👤' });
        return;
      }
      const activePhone = callerPhone?.trim() || user?.phone?.trim();
      if (!activePhone) {
        toast.error('Phone number is mandatory to place a call.', { icon: '📱' });
        return;
      }
      if (!customReason && (!callReason || !callReason.trim())) {
        toast.error('Reason for calling is mandatory.', { icon: '📝' });
        return;
      }
    }

    setOfflineNotice(false);
    setCallbackSuccess(false);
    setIsOpen(false);

    setActiveCallInfo({
      isIntercom,
      peerName: targetName || (isIntercom ? 'Team Member' : 'Support Desk'),
      targetUserId
    });

    try {
      if (!isIntercom) {
        waitingTonePlayer.start(waitingTone);
        // Play smooth brand welcome voiceover over the soft orchestra
        setTimeout(() => {
          const activeName = (guestNameRef.current || guestName || user?.name || '').trim();
          const brandIntro = activeName
            ? `Hello ${activeName}! Thank you for calling EpicEdge Creative. Please hold while we connect you to our support team.`
            : `Thank you for calling EpicEdge Creative. Please hold while we connect you to our support team.`;

          waitingTonePlayer.duck(0.04);
          playNeuralAudio(brandIntro, voicePersona, () => {
            waitingTonePlayer.unduck();
          });
        }, 700);
      }

      await startCall({
        targetUserId,
        targetName,
        callerName: user?.name || guestName || 'Guest Visitor',
        callerPhone: callerPhone?.trim() || user?.phone || '',
        callerRole: user?.role || 'client',
        projectId: null,
        reason: customReason || callReason || (isIntercom ? 'Internal Intercom Call' : 'Direct Voice Consultation'),
        isIntercom
      });
    } catch (err) {
      waitingTonePlayer.stop();
      stopAiVoice();
      console.error('Call failed:', err);
    }
  }, [startCall, user, guestName, callerPhone, callReason, isAuthenticated, waitingTone, voicePersona]);

  // Handle Answer Call
  const handleAnswer = () => {
    if (!incomingCall) return;
    ringtone.stop();
    setActiveCallInfo({
      isIntercom: !!incomingCall.isIntercom,
      peerName: incomingCall.callerName,
      peerRole: incomingCall.callerRole
    });
    answerCall(incomingCall.callerSocketId);
    setIncomingCall(null);
  };

  // Handle Decline Call
  const handleDecline = () => {
    if (!incomingCall) return;
    ringtone.stop();
    rejectCall(incomingCall.callerSocketId);
    setIncomingCall(null);
    setActiveCallInfo(null);
  };

  // Submit Callback Request
  const handleSubmitCallback = async (e) => {
    e.preventDefault();
    setIsSubmittingCallback(true);

    try {
      await axios.post(getApiUrl('/api/calls/callbacks'), {
        callerId: user?._id || null,
        callerName: user?.name || guestName || 'Valued Client',
        callerPhone: callbackPhone || user?.phone,
        callerEmail: user?.email || null,
        reason: callbackNotes || 'Client requested a callback'
      });

      setCallbackSuccess(true);
      toast.success('Callback requested! Our team will call you shortly.');
      setTimeout(() => {
        setOfflineNotice(false);
        setIsOpen(false);
        setCallbackSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error submitting callback:', err);
      toast.error('Could not submit callback request. Please try again.');
    } finally {
      setIsSubmittingCallback(false);
    }
  };

  // Format call duration (MM:SS)
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Filtered team directory for intercom
  const filteredTeam = teamMembers
    .filter(m => m._id !== user?._id) // Don't show self
    .filter(m => {
      if (!intercomSearch) return true;
      const q = intercomSearch.toLowerCase();
      return (
        m.name?.toLowerCase().includes(q) ||
        m.role?.toLowerCase().includes(q) ||
        m.email?.toLowerCase().includes(q)
      );
    });

  // Require valid name, phone, and reason before enabling the call button
  const canCall = Boolean(
    (isAuthenticated || (guestName && guestName.trim().length >= 2)) &&
    ((callerPhone && callerPhone.trim().length >= 5) || (user?.phone && user.phone.trim().length >= 5)) &&
    (callReason && callReason.trim().length >= 2)
  );

  return (
    <>
      {/* 1. INCOMING CALL MODAL */}
      {incomingCall && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md animate-in fade-in duration-200">
          <div className="relative w-full max-w-sm p-6 bg-white dark:bg-onyx-900 border border-slate-200 dark:border-onyx-800 rounded-3xl shadow-2xl text-center">
            {/* Pulsing Beacon (Intercom vs Client Call) */}
            <div className="relative mx-auto w-20 h-20 mb-4 flex items-center justify-center">
              <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping ${
                incomingCall.isIntercom ? 'bg-indigo-500' : 'bg-emerald-400'
              }`} />
              <div className={`relative w-16 h-16 rounded-full flex items-center justify-center shadow-lg text-white ${
                incomingCall.isIntercom 
                  ? 'bg-gradient-to-tr from-indigo-600 via-purple-600 to-cyan-500 shadow-indigo-500/40' 
                  : 'bg-gradient-to-tr from-emerald-600 to-teal-500 shadow-emerald-500/30'
              }`}>
                {incomingCall.isIntercom ? (
                  <Radio className="w-8 h-8 animate-pulse" />
                ) : (
                  <PhoneCall className="w-8 h-8 animate-bounce" />
                )}
              </div>
            </div>

            <div className={`inline-block px-3 py-1 mb-2 text-[10px] font-black uppercase tracking-widest rounded-full ${
              incomingCall.isIntercom 
                ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800' 
                : 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400'
            }`}>
              {incomingCall.isIntercom ? '⚡ Internal Team Intercom' : 'Inbound Client Call'}
            </div>

            <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">
              {incomingCall.callerName}
            </h3>
            <p className="text-xs text-slate-500 dark:text-gray-400 font-bold mt-0.5">
              Role: <span className="uppercase text-slate-800 dark:text-gray-200 font-black">{incomingCall.callerRole}</span>
            </p>

            {incomingCall.callerPhone && (
              <p className="text-xs font-black text-emerald-600 dark:text-emerald-400 mt-1 flex items-center justify-center gap-1.5">
                <Phone className="w-3.5 h-3.5" />
                {incomingCall.callerPhone}
              </p>
            )}

            {incomingCall.reason && (
              <p className="mt-2 text-[11px] text-slate-600 dark:text-gray-300 italic bg-slate-50 dark:bg-onyx-950 p-2.5 rounded-xl border border-slate-100 dark:border-onyx-800">
                "{incomingCall.reason}"
              </p>
            )}

            <div className="mt-6 flex gap-3">
              <button
                onClick={handleDecline}
                className="flex-1 py-3 bg-rose-50 hover:bg-rose-100 text-rose-600 dark:bg-rose-950/40 dark:hover:bg-rose-900/50 dark:text-rose-400 rounded-2xl text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 border border-rose-200 dark:border-rose-900/50"
              >
                <PhoneOff className="w-4 h-4" /> Decline
              </button>
              <button
                onClick={handleAnswer}
                className={`flex-1 py-3 text-white rounded-2xl text-xs font-black uppercase tracking-widest shadow-lg transition-all flex items-center justify-center gap-2 ${
                  incomingCall.isIntercom
                    ? 'bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-500 hover:to-cyan-500 shadow-indigo-500/25'
                    : 'bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 shadow-emerald-500/25'
                }`}
              >
                <Phone className="w-4 h-4" /> Accept
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. ACTIVE CALL FLOATING BAR */}
      {(callState === 'calling' || callState === 'connected') && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-6 py-3.5 bg-slate-900/95 text-white rounded-full shadow-2xl border border-white/10 backdrop-blur-md animate-in slide-in-from-bottom duration-300">
          <div className="flex items-center gap-2.5">
            <span className={`w-3 h-3 rounded-full ${
              callState === 'connected' 
                ? (activeCallInfo?.isIntercom ? 'bg-cyan-400 shadow-[0_0_10px_#22d3ee] animate-pulse' : 'bg-emerald-500 shadow-[0_0_10px_#10b981] animate-pulse') 
                : 'bg-amber-400 animate-ping'
            }`} />
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-300">
                {callState === 'connected' 
                  ? (activeCallInfo?.isIntercom ? `⚡ Intercom: ${activeCallInfo?.peerName || 'Team'}` : `Voice Call: ${activeCallInfo?.peerName || 'Connected'}`)
                  : (activeCallInfo?.isIntercom ? 'Connecting Intercom...' : '🎻 Connecting Line • Playing Waiting Melody...')}
              </p>
              {callState === 'connected' && (
                <p className="text-xs font-mono font-bold text-emerald-400">
                  {formatDuration(callDuration)}
                </p>
              )}
            </div>
          </div>

          <div className="h-6 w-[1px] bg-white/10 mx-1" />

          {/* Mute Toggle */}
          <button
            onClick={toggleMute}
            className={`p-2.5 rounded-full transition-all ${isMuted ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/20' : 'bg-white/10 hover:bg-white/20 text-white'}`}
            title={isMuted ? 'Unmute Microphone' : 'Mute Microphone'}
          >
            {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>

          {/* Hang Up */}
          <button
            onClick={endCall}
            className="p-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-full shadow-lg shadow-rose-600/30 transition-all flex items-center justify-center"
            title="Hang Up Call"
          >
            <PhoneOff className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 2.5 EPIC AI VOICE RECEPTIONIST MODAL */}
      {isAiCallActive && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-xl animate-in fade-in duration-300">
          <div className="relative w-full max-w-md bg-gradient-to-b from-slate-900 via-onyx-900 to-slate-950 border border-purple-500/20 rounded-3xl shadow-2xl overflow-hidden text-center p-6 text-white">
            
            {/* Top Bar */}
            <div className="flex items-center justify-between pb-4 border-b border-white/10 mb-6">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[10px] font-black uppercase tracking-widest text-purple-300 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                  Epic AI Voice Receptionist
                </span>
              </div>
              <div className="text-xs font-mono font-bold text-slate-400">
                {formatDuration(aiDuration)}
              </div>
            </div>

            {aiCallStatus === 'ended' ? (
              /* CALL ENDED STATE */
              <div className="py-8">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-black uppercase tracking-tight text-white mb-1">
                  Call Completed
                </h3>
                <p className="text-xs text-slate-300 max-w-xs mx-auto mb-3">
                  Thank you! Your call has been logged and our team has been notified. We will review your inquiry and follow up shortly.
                </p>
                <div className="flex items-center justify-center gap-2 text-[11px] font-bold text-emerald-400 mb-6 bg-emerald-500/10 py-1.5 px-3.5 rounded-full w-fit mx-auto border border-emerald-500/20">
                  <Clock className="w-3.5 h-3.5 animate-spin" />
                  <span>Redirecting to homepage in {redirectCountdown !== null ? redirectCountdown : 3}s...</span>
                </div>
                <button
                  onClick={() => {
                    if (redirectTimerRef.current) clearInterval(redirectTimerRef.current);
                    setIsAiCallActive(false);
                    setAiCallStatus('idle');
                    resetCallForm();
                    navigate('/');
                  }}
                  className="px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer"
                >
                  Close & Go Home Now
                </button>
              </div>
            ) : (
              /* ACTIVE CONVERSATION STATE */
              <>
                {/* Animated AI Voice Avatar Orb */}
                <div className="relative mx-auto w-28 h-28 my-2 flex items-center justify-center">
                  {/* Outer pulsating wave rings */}
                  <span className={`absolute inline-flex h-full w-full rounded-full opacity-40 animate-ping duration-1000 ${
                    aiCallStatus === 'speaking'
                      ? 'bg-purple-500'
                      : aiCallStatus === 'thinking'
                      ? 'bg-amber-500'
                      : 'bg-cyan-400'
                  }`} />
                  <span className={`absolute inline-flex h-24 w-24 rounded-full opacity-60 animate-pulse ${
                    aiCallStatus === 'speaking'
                      ? 'bg-gradient-to-tr from-purple-600 to-pink-500'
                      : aiCallStatus === 'thinking'
                      ? 'bg-gradient-to-tr from-amber-500 to-orange-600'
                      : 'bg-gradient-to-tr from-cyan-500 to-emerald-500'
                  }`} />
                  
                  {/* Center Core */}
                  <div className="relative w-20 h-20 rounded-full bg-slate-900 border-2 border-white/20 flex items-center justify-center shadow-2xl">
                    {aiCallStatus === 'speaking' ? (
                      <Volume2 className="w-9 h-9 text-purple-300 animate-bounce" />
                    ) : aiCallStatus === 'thinking' ? (
                      <Loader2 className="w-9 h-9 text-amber-300 animate-spin" />
                    ) : isAiMuted ? (
                      <MicOff className="w-9 h-9 text-rose-400" />
                    ) : (
                      <Mic className="w-9 h-9 text-cyan-300 animate-pulse" />
                    )}
                  </div>
                </div>

                {/* Status Badge */}
                <div className="mt-3 mb-4">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                    aiCallStatus === 'speaking'
                      ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                      : aiCallStatus === 'thinking'
                      ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                      : isAiMuted
                      ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                      : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                  }`}>
                    {aiCallStatus === 'speaking' && '🎙️ Epic AI is speaking...'}
                    {aiCallStatus === 'thinking' && '⚡ Formulating response...'}
                    {aiCallStatus === 'listening' && (isAiMuted ? '🔇 Microphone Muted' : '🟢 Call Connected • Speak Freely')}
                    {aiCallStatus === 'connecting' && '⚡ Connecting voice line...'}
                  </span>
                </div>

                {/* Controls Bar (Pure Voice Phone Call - No text/typing) */}
                <div className="flex items-center justify-center gap-4 pt-6 pb-2">
                  {/* Mute Mic */}
                  <button
                    onClick={() => {
                      setIsAiMuted(m => {
                        const next = !m;
                        isAiMutedRef.current = next;
                        if (next && recognitionInstanceRef.current) {
                          try { recognitionInstanceRef.current.abort(); } catch (e) {}
                        } else if (!next && !isAiSpeakingRef.current && !isAiThinkingRef.current) {
                          startListeningRef.current?.();
                        }
                        return next;
                      });
                    }}
                    className={`p-3.5 rounded-2xl border transition-all ${
                      isAiMuted
                        ? 'bg-rose-500/20 border-rose-500/40 text-rose-400'
                        : 'bg-white/10 hover:bg-white/20 border-white/10 text-white'
                    }`}
                    title={isAiMuted ? 'Unmute Microphone' : 'Mute Microphone'}
                  >
                    {isAiMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                  </button>

                  {/* End Call */}
                  <button
                    onClick={endAiVoiceCall}
                    disabled={isAiSaving}
                    className="px-8 py-3.5 bg-rose-600 hover:bg-rose-700 text-white rounded-2xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2.5 shadow-lg shadow-rose-600/30 active:scale-95"
                  >
                    <PhoneOff className="w-4 h-4" />
                    {isAiSaving ? 'Saving Call...' : 'End Call'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 3. FLOATING TRIGGER BUTTON (Bottom-Right, stacked above AI Chat Widget) */}
      <div className="fixed bottom-24 right-6 z-40">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative group px-2.5 py-1.5 bg-gradient-to-tr from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white rounded-xl shadow-md shadow-emerald-500/20 transition-all duration-300 flex items-center gap-1.5 hover:scale-105 active:scale-95 border border-white/15"
          title={isTeam ? 'EpicEdge Voice Console & Intercom' : 'Call Live Support'}
        >
          {isTeam ? <Radio className="w-3.5 h-3.5" /> : <Headphones className="w-3.5 h-3.5" />}
          <span className="text-[10px] font-black uppercase tracking-wider">
            {isTeam ? 'Intercom' : 'Support'}
          </span>
          {/* Online green indicator */}
          <span className="w-1.5 h-1.5 bg-emerald-300 rounded-full animate-pulse shadow-[0_0_6px_#34d399]" />
        </button>
      </div>

      {/* 4. CALL & CALLBACK DIALOG DRAWER */}
      {isOpen && (
        <div className="fixed bottom-40 right-6 z-40 w-84 sm:w-96 bg-white dark:bg-onyx-900 border border-slate-200 dark:border-onyx-800 rounded-3xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200">
          <div className="p-4 bg-gradient-to-r from-slate-900 via-slate-850 to-slate-800 text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PhoneCall className="w-4 h-4 text-emerald-400" />
              <h4 className="text-xs font-black uppercase tracking-wider">
                {isTeam ? 'Voice & Intercom Station' : 'Voice Connect'}
              </h4>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-slate-400 hover:text-white p-1 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Team Tabs (Console vs Intercom) */}
          {isTeam && !offlineNotice && (
            <div className="flex border-b border-slate-100 dark:border-onyx-800 bg-slate-50/50 dark:bg-onyx-950/50 p-1.5 gap-1.5">
              <button
                type="button"
                onClick={() => setActiveTab('console')}
                className={`flex-1 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 ${
                  activeTab === 'console'
                    ? 'bg-white dark:bg-onyx-900 text-emerald-600 dark:text-emerald-400 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-gray-300'
                }`}
              >
                <Headphones className="w-3.5 h-3.5" /> Client Line ({adminCallbacks.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('intercom')}
                className={`flex-1 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 ${
                  activeTab === 'intercom'
                    ? 'bg-white dark:bg-onyx-900 text-indigo-600 dark:text-indigo-400 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-gray-300'
                }`}
              >
                <Radio className="w-3.5 h-3.5 text-indigo-500" /> 
                Team Intercom
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
              </button>
            </div>
          )}

          <div className="p-5">
            {offlineNotice ? (
              /* OFFLINE / BUSY CALLBACK FORM */
              <div>
                <div className="p-3 mb-4 rounded-2xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 flex items-start gap-2.5">
                  <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <h5 className="text-[11px] font-black text-amber-800 dark:text-amber-300 uppercase tracking-wide">
                      We'd Love to Connect With You
                    </h5>
                    <p className="text-[10px] text-amber-700 dark:text-gray-300 mt-0.5">
                      We value your time. Leave your details below and our team will personally call you right back as a priority!
                    </p>
                  </div>
                </div>

                {callbackSuccess ? (
                  <div className="py-6 text-center text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="w-10 h-10 mx-auto mb-2" />
                    <p className="text-xs font-black uppercase tracking-wider">Callback Requested!</p>
                    <p className="text-[10px] text-slate-500 mt-1">We'll reach out shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmitCallback} className="flex flex-col gap-3">
                    <div>
                      <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={callbackPhone}
                        onChange={(e) => setCallbackPhone(e.target.value)}
                        placeholder="e.g. +254 700 000000"
                        className="w-full px-3 py-2 bg-slate-50 dark:bg-onyx-950 border border-slate-200 dark:border-onyx-800 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-emerald-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">
                        Topic / Reason
                      </label>
                      <textarea
                        value={callbackNotes}
                        onChange={(e) => setCallbackNotes(e.target.value)}
                        placeholder="Project updates, milestone review, etc."
                        rows={2}
                        className="w-full px-3 py-2 bg-slate-50 dark:bg-onyx-950 border border-slate-200 dark:border-onyx-800 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                      />
                    </div>
                    <div className="flex gap-2 mt-1">
                      <button
                        type="button"
                        onClick={() => setOfflineNotice(false)}
                        className="flex-1 py-2 text-slate-500 text-[10px] font-black uppercase tracking-wider hover:underline"
                      >
                        Back
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmittingCallback}
                        className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-md shadow-emerald-600/20"
                      >
                        {isSubmittingCallback ? 'Submitting...' : 'Request Callback'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            ) : isTeam && activeTab === 'intercom' ? (
              /* INTERNAL TEAM INTERCOM DIRECTORY */
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-tight text-slate-900 dark:text-white flex items-center gap-1.5">
                      <Zap className="w-3.5 h-3.5 text-indigo-500" /> Internal Intercalls
                    </h4>
                    <p className="text-[10px] text-slate-500 font-bold">
                      Direct 1-click HD voice line to team members.
                    </p>
                  </div>
                  <span className="px-2 py-0.5 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 text-[9px] font-black uppercase rounded-full">
                    {onlineTeam.length} Active
                  </span>
                </div>

                {/* Search staff */}
                <div className="relative mb-3">
                  <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
                  <input
                    type="text"
                    value={intercomSearch}
                    onChange={(e) => setIntercomSearch(e.target.value)}
                    placeholder="Search staff by name or role..."
                    className="w-full pl-8 pr-3 py-1.5 bg-slate-50 dark:bg-onyx-950 border border-slate-200 dark:border-onyx-800 rounded-xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* Team member list */}
                <div className="max-h-56 overflow-y-auto space-y-2 pr-1">
                  {filteredTeam.length > 0 ? (
                    filteredTeam.map(member => {
                      const onlineInfo = onlineTeam.find(o => o.userId?.toString() === member._id?.toString());
                      const isOnline = !!onlineInfo;
                      const isBusy = onlineInfo?.isBusy;

                      return (
                        <div
                          key={member._id}
                          className="p-2.5 bg-slate-50 dark:bg-onyx-950 rounded-2xl border border-slate-200 dark:border-onyx-800 flex items-center justify-between transition-all hover:border-indigo-400/50"
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="relative shrink-0">
                              <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-indigo-600 to-cyan-500 text-white font-black text-xs flex items-center justify-center uppercase shadow-sm">
                                {member.name ? member.name.charAt(0) : 'U'}
                              </div>
                              <span
                                className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-onyx-900 ${
                                  isBusy
                                    ? 'bg-amber-400'
                                    : isOnline
                                    ? 'bg-emerald-500'
                                    : 'bg-slate-300 dark:bg-slate-600'
                                }`}
                              />
                            </div>
                            <div className="min-w-0">
                              <h5 className="text-[11px] font-black text-slate-900 dark:text-white uppercase truncate">
                                {member.name}
                              </h5>
                              <p className="text-[9px] text-slate-500 font-bold uppercase truncate">
                                {member.role} {isBusy && '• (In Call)'}
                              </p>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => handleStartCall(member._id, 'Internal Team Intercom', member.name, true)}
                            disabled={callState === 'calling' || isBusy}
                            className={`px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-sm ${
                              isBusy
                                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                : isOnline
                                ? 'bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-500 hover:to-cyan-500 text-white shadow-indigo-500/20'
                                : 'bg-slate-100 dark:bg-onyx-800 text-slate-600 dark:text-gray-300 hover:bg-slate-200'
                            }`}
                            title={isOnline ? 'Direct Intercom Call' : 'Target is offline (will ring or notify)'}
                          >
                            <Radio className="w-3 h-3" />
                            {isBusy ? 'Busy' : 'Intercall'}
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <div className="p-4 bg-slate-50 dark:bg-onyx-950 rounded-xl border border-dashed border-slate-200 dark:border-onyx-800 text-center">
                      <Users className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                      <p className="text-[10px] font-bold text-slate-500">
                        {intercomSearch ? 'No matching team members' : 'No other team members found'}
                      </p>
                    </div>
                  )}
                </div>

                <div className="mt-3 pt-2.5 border-t border-slate-100 dark:border-onyx-800 text-center">
                  <a
                    href="/admin-calls"
                    onClick={() => setIsOpen(false)}
                    className="text-[10px] font-black text-indigo-500 hover:underline uppercase"
                  >
                    View Complete Call & Intercom Logs →
                  </a>
                </div>
              </div>
            ) : isAdminOrManager ? (
              /* ADMIN INBOUND VOICE STATION */
              <div>
                <div className="text-center mb-4">
                  <div className="w-12 h-12 mx-auto rounded-2xl bg-slate-900 dark:bg-onyx-950 text-emerald-400 flex items-center justify-center mb-2 border border-emerald-500/20 shadow-md">
                    <Headphones className="w-6 h-6 animate-pulse" />
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 text-[9px] font-black uppercase tracking-wider mb-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                    Operator Station Active
                  </div>
                  <h4 className="text-xs font-black uppercase tracking-tight text-slate-900 dark:text-white">
                    Admin Voice Console
                  </h4>
                  <p className="text-[10px] text-slate-500 font-bold mt-0.5">
                    Your line is active to receive inbound client voice calls.
                  </p>
                </div>

                {/* Pending Callbacks Quick Access */}
                <div className="mb-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                      Pending Callbacks ({adminCallbacks.length})
                    </span>
                    <a
                      href="/admin-calls"
                      onClick={() => setIsOpen(false)}
                      className="text-[9px] font-black text-amber-500 hover:underline uppercase"
                    >
                      View Logs
                    </a>
                  </div>

                  {adminCallbacks.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                      {adminCallbacks.map(cb => (
                        <div key={cb._id} className="p-2.5 bg-slate-50 dark:bg-onyx-950 rounded-xl border border-slate-200 dark:border-onyx-800 text-left">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] font-black text-slate-900 dark:text-white uppercase truncate max-w-[130px]">
                              {cb.callerName}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                handleStartCall(cb.callerId, `Callback to ${cb.callerName}`, cb.callerName, false);
                              }}
                              className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-[9px] font-black uppercase tracking-wider transition-all flex items-center gap-1 shadow-sm"
                            >
                              <Phone className="w-2.5 h-2.5" /> Call
                            </button>
                          </div>
                          <p className="text-[9px] text-slate-500 font-bold truncate mt-0.5">
                            {cb.callerPhone || cb.callerEmail || 'No phone provided'}
                          </p>
                          {cb.reason && (
                            <p className="text-[9px] text-slate-600 dark:text-gray-400 italic truncate mt-0.5">
                              "{cb.reason}"
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 bg-slate-50 dark:bg-onyx-950 rounded-xl border border-dashed border-slate-200 dark:border-onyx-800 text-center">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                      <p className="text-[10px] font-bold text-slate-500">
                        No pending callbacks. Inbound calls from clients will ring directly here.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* LIVE CALL START PANEL (CLIENT / VISITOR) */
              <div>
                <div className="text-center mb-4">
                  <div className="w-12 h-12 mx-auto rounded-2xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-2">
                    <Phone className="w-6 h-6" />
                  </div>
                  <h4 className="text-xs font-black uppercase tracking-tight text-slate-900 dark:text-white">
                    Direct Voice Line
                  </h4>
                  <p className="text-[10px] text-slate-500 font-bold mt-0.5">
                    Connect instantly to our project team via WebRTC high-definition voice.
                  </p>
                </div>

                <form onSubmit={(e) => {
                  e.preventDefault();
                  if (!canCall) {
                    toast.error('Please fill in your name and phone number first.');
                    return;
                  }
                  handleStartCall();
                }}>
                  {!isAuthenticated && (
                    <div className="mb-3">
                      <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">
                        Your Name
                      </label>
                      <input
                        type="text"
                        required
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        placeholder="e.g. John Doe"
                        className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-onyx-950 border border-slate-200 dark:border-onyx-800 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      required
                      value={callerPhone}
                      onChange={(e) => setCallerPhone(e.target.value)}
                      placeholder="e.g. +254 700 000000"
                      className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-onyx-950 border border-slate-200 dark:border-onyx-800 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">
                      Reason for Calling <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={callReason}
                      onChange={(e) => setCallReason(e.target.value)}
                      placeholder="e.g. Website development, quote inquiry"
                      className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-onyx-950 border border-slate-200 dark:border-onyx-800 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={!canCall || callState === 'calling'}
                    className={`w-full py-3.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${
                      canCall && callState !== 'calling'
                        ? 'bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white shadow-lg shadow-emerald-500/25 cursor-pointer active:scale-98'
                        : 'bg-slate-200 dark:bg-onyx-800 text-slate-400 dark:text-gray-500 cursor-not-allowed opacity-60'
                    }`}
                  >
                    <Phone className="w-4 h-4" />
                    {callState === 'calling' 
                      ? 'Calling...' 
                      : canCall 
                      ? 'Call Support Now' 
                      : 'Fill Details to Call'}
                  </button>
                </form>

                <div className="mt-3 text-center">
                  <button
                    type="button"
                    onClick={() => setOfflineNotice(true)}
                    className="text-[10px] font-bold text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  >
                    Want us to call you instead? <span className="underline font-black text-emerald-500">Request Callback</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ADMIN FLOATING BANNER: LIVE AI CALL TAKEOVER */}
      {isTeam && activeAiCalls.length > 0 && callState === 'idle' && !isAiCallActive && (
        <div className="fixed top-4 right-4 z-[99999] flex flex-col gap-2 max-w-sm">
          {activeAiCalls.map((call) => (
            <div
              key={call.callerSocketId}
              className="p-3.5 bg-slate-900/95 dark:bg-onyx-950/95 backdrop-blur-md border-2 border-emerald-500 rounded-2xl shadow-2xl shadow-emerald-500/25 text-white flex items-center justify-between gap-3 animate-pulse"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="relative flex h-3 w-3 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Bot className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-400 truncate">
                      AI Call in Progress
                    </span>
                  </div>
                  <p className="text-xs font-bold text-white truncate">
                    {call.callerName || 'Guest Visitor'}
                  </p>
                  {call.callerPhone && (
                    <p className="text-[10px] text-slate-400 truncate">{call.callerPhone}</p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleTakeoverCall(call.callerSocketId, call.callerName)}
                className="shrink-0 px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white text-[11px] font-black uppercase tracking-wider rounded-xl shadow-md transition-all active:scale-95 flex items-center gap-1.5 cursor-pointer"
              >
                <PhoneCall className="w-3.5 h-3.5" />
                Take Over
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default FloatingCallWidget;
